<template>
  <section
    class="buy-now"
    :class="[
      {
        loading: isLoading,
        fadeout: !isLoading,
        'limit-results': limitResults,
      },
      campaignConfig.preset,
    ]"
  >
    <div class="loader"></div>
    <img
      class="product"
      :src="campaignConfig.productImage"
      :alt="campaignConfig.productAltText"
    />
    <h3 v-if="campaignConfig.productName && campaignConfig.productName !== ''">
      {{ campaignConfig.productName }}
    </h3>
    <div
      v-if="
        campaignConfig.productDescription &&
        campaignConfig.productDescription !== ''
      "
      v-html="campaignConfig.productDescription"
    ></div>
    <p v-if="variants.length > 0 && campaignConfig.selectedEan">
      <strong>Select variant</strong>
      <span class="select-wrap">
        <select
          name="variants"
          id="variants"
          v-on:change="loadRetailer"
          v-model="campaignConfig.selectedEan"
        >
          <template v-for="(variant, v) in variants">
            <option v-bind:key="variant.ean" :value="variant.ean">
              {{ variant.label }}
            </option>
          </template>
        </select>
      </span>
    </p>
    <div class="results" :class="campaignConfig.ctaType">
      <template v-for="(retailer, i) in retailers">
        <a
          v-bind:key="retailer.id"
          :href="retailer.deeplink"
          :title="retailer.name"
          class="shop"
          v-bind:class="{ 'hide-retailer': i >= campaignConfig.nrOfRetailers }"
          target="_blank"
        >
          <img :src="retailer.logo" :alt="retailer.name" />
          <span class="shop-name">{{ retailer.name }}</span>
          <span
            v-if="campaignConfig.ctaType === 'custom'"
            class="btn"
            :style="campaignConfig.btnStyling"
            >{{ campaignConfig.buyNowCTACaption }}</span
          >
          <span
            v-if="campaignConfig.ctaType === 'default'"
            class="btn"
            :style="campaignConfig.btnStyling"
            >{{ $t("Buy now") }}</span
          >
        </a>
      </template>
    </div>
    <div class="center">
      <a href="#" class="btn show-more" v-on:click="showMore()">{{
        $t("Show more")
      }}</a>
    </div>
    <footer>
      {{
        $t(
          "We update the inventory information regularly - subject to prior sale."
        )
      }}
      <a
        href="https://www.commerce-connector.com/web/en/policy-cco/"
        target="_blank"
        >{{ $t("More information") }}</a
      >
      <a href="http://www.commerce-connector.com/" target="_blank"
        ><img src="../assets/cc-logo.png" alt="Commerce Connector"
      /></a>
    </footer>
  </section>
</template>

<script>
import { CCAPI } from "./CommerceConnectorAPI";
import { CampaignAPI } from "./CampaignAPI";
import CommerceConnectorOnlineShop from "./CommerceConnectorOnlineShop";
import VueGtm from "vue-gtm";
import Vue from "vue";

const getDeviceType = () => {
  const ua = navigator.userAgent;
  const regex = {
    mobile:
      /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/,
    tablet: /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i,
  };

  if (regex.tablet.test(ua)) {
    return "tablet";
  }

  if (regex.mobile.test(ua)) {
    return "mobile";
  }

  return "desktop";
};

export default {
  name: "BuyNowAds",
  components: {
    CCAPI,
    CampaignAPI,
    CommerceConnectorOnlineShop,
  },
  data() {
    return {
      retailers: [],
      variants: [],
      campaignConfig: {
        selectedEan: "",
        campaignName: "",
        productName: "",
        productDescription: "",
        productImage: "",
        productAltText: "",
        preset: "preset-1",
        buyNowCTACaption: "Buy now",
        btnStyling: {
          background: "#FF0000",
          color: "#FFF",
        },
        nrOfRetailers: 6,
      },
      isLoading: true,
      limitResults: true,
      campaignLoaded: false,
    };
  },
  created() {
    // check if we have a matching route
    if (
      this.$route.params.campaignHash &&
      this.$route.params.campaignHash !== ""
    ) {
      // load campaign config
      CampaignAPI.get(
        `c/${this.$route.params.campaignHash}/${this.$route.params.productHash}`
      )
        .then((res) => {
          if (res.data.redirectURL) {
            window.location = res.data.redirectURL;
            return;
          }

          this.$i18n.locale = `${res.data.language}_${res.data.country}`;
          document.title =
            res.data.productData.name || this.$i18n.t("Buy now online.");

          this.campaignConfig.campaignName = res.data.name;
          this.campaignConfig.productName = res.data.productData.name;
          this.campaignConfig.productDescription =
            res.data.productData.description;
          this.campaignConfig.preset = res.data.productData.designPreset;
          this.campaignConfig.buyNowCTACaption =
            res.data.productData.buyNowCTACaption ||
            this.$i18n.t(this.campaignConfig.buyNowCTACaption);
          this.campaignConfig.productImage = res.data.productData.productImage;
          this.campaignConfig.productAltText =
            res.data.productData.productImageAlt;

          this.campaignConfig.nrOfRetailers =
            res.data.productData.nrOfRetailers ||
            this.campaignConfig.nrOfRetailers;

          this.campaignConfig.btnStyling.background =
            res.data.productData.buyNowCTABgColor ||
            this.campaignConfig.btnStyling.background;
          this.campaignConfig.btnStyling.color =
            res.data.productData.buyNowCTAColor ||
            this.campaignConfig.btnStyling.color;
          this.campaignConfig.ctaType =
            res.data.productData.ctaType || "default";
          this.campaignConfig.maxDeliveryTime =
            res.data.maxDeliveryTime || false;

          const productIdentifierMap = {
            ean: "F_Ean",
            pzn: "F_Pzn",
            man: "F_ManufacturerArticleNumber",
          };

          const productIdentifierParam =
            productIdentifierMap[res.data.productData.productIdentifier];
          this.campaignConfig.productIdentifier = productIdentifierParam;

          if (!productIdentifierParam) {
            if (res.data.redirectURL) {
              window.location = res.data.redirectURL;
            } else {
              this.$router.push("/404");
            }
          }

          const urlParams = {
            token: res.data.token,
            Country: res.data.country,
            Language: res.data.language,
            O_ResultFields:
              "Id,Name,Logos,Currency,OfferInfo,Currency,AddressInfo",
            "O_ResultAmount[Limit]": this.campaignConfig.nrOfRetailers || 10,
            productLine: "bna",
            deviceType: getDeviceType(),
          };

          urlParams[productIdentifierParam] = this.campaignConfig.selectedEan =
            res.data.productData.productId;

          if (
            res.data.productData.trackingId &&
            res.data.productData.trackingId !== ""
          ) {
            urlParams.trackingId = res.data.productData.trackingId;
          } else {
            // use the custom tracking id passed from the url instead
            if (this.$route.query.ccTid && this.$route.query.ccTid !== "") {
              urlParams.trackingId = this.$route.query.ccTid;
            }
          }

          if (res.data.gtmId) {
            Vue.use(VueGtm, {
              id: res.data.gtmId, // Your GTM ID
            });
          }

          // dont send subid filter if "ohne subid" is selected
          if (
            res.data.productData.subId &&
            res.data.productData.subId !== "ohne" &&
            res.data.productData.subId !== "default"
          ) {
            urlParams.F_SubId = res.data.productData.subId;
          }
          this.campaignConfig.urlParams = urlParams;
          this.campaignLoaded = true;

          this.loadRetailer();
          this.loadVariants();
        })
        .catch((e) => {
          console.log(e);
          this.$router.push("/404");
        });
    }
  },
  methods: {
    showMore() {
      this.limitResults = false;
    },
    loadRetailer(e) {
      if (e) {
        this.campaignConfig.urlParams.F_Ean = this.campaignConfig.selectedEan;
      }

      CCAPI.get("OnlineShop", { params: this.campaignConfig.urlParams })
        .then((res) => {
          this.isLoading = false;
          this.retailers = CommerceConnectorOnlineShop.getShops(
            res.data.getResult[0].ResultGroups[0].OnlineShops,
            this.campaignConfig
          );
          this.limitResults =
            this.retailers.length > this.campaignConfig.nrOfRetailers;
        })
        .catch((e) => {
          console.log(e);
        });
    },
    loadVariants() {
      this.isLoading = true;
      const params = {
        token: this.campaignConfig.urlParams.token,
        Country: this.campaignConfig.urlParams.Country,
        Language: this.campaignConfig.urlParams.Language,
      };

      params[this.campaignConfig.productIdentifier] =
        this.campaignConfig.urlParams[this.campaignConfig.productIdentifier];

      CCAPI.get("Article", { params: params }).then((res) => {
        this.isLoading = false;
        this.variants = res.data.map((variant) => {
          return { ean: variant.ean, label: variant.varName };
        });
      });
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
strong {
  padding-left: 1%;
}

h3 {
  font-weight: normal;
}

p,
div {
  margin-top: 0;
  text-align: center;
  overflow: hidden;
}

h3,
p,
div {
  padding: 2% 4%;
  margin-bottom: 0;
}

footer a {
  display: block;
}

.center {
  text-align: center;
}

strong,
h3 {
  display: block;
  text-align: center;
}

.buy-now {
  margin-top: 15px;
}

.loader {
  -webkit-transition: opacity 0.3s;
  -moz-transition: opacity 0.3s;
  -ms-transition: opacity 0.3s;
  -o-transition: opacity 0.3s;
  transition: opacity 0.3s;
  padding: 0;
}

.fadeout {
  .loader {
    opacity: 0;
  }
}

.show-more {
  display: none;
  background: #ccc;
  padding: 5px 10px;
  color: #fff;
  margin-top: 10px;
}

.limit-results {
  .show-more {
    display: inline-block;
  }

  .shop:nth-of-type(n + 7) {
    display: none;
  }

  .shop.hide-retailer {
    display: none;
  }
}

.loading {
  .loader {
    opacity: 1;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    background: #fff;
    z-index: 10;

    &:after {
      content: "";
      background: url(../assets/loader.svg) no-repeat center center;
      background-size: contain;
      height: 40px;
      width: 40px;
      position: absolute;
      left: 50%;
      top: 50%;
      -webkit-transform: translate(-50%, -50%);
      -moz-transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
    }
  }
}

p > strong {
  padding-left: 0;
  text-align: left;
}

.select-wrap {
  display: inline-block;
  position: relative;
  width: 100%;

  &:after {
    content: "";
    position: absolute;
    right: 10px;
    top: 50%;
    -webkit-transform: translateY(-50%);
    -moz-transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    -o-transform: translateY(-50%);
    transform: translateY(-50%);
    display: block;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #000;
    pointer-events: none;
  }
}

select {
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: 1px solid rgba(34, 36, 38, 0.15);
  padding: 11px 30px 11px 14px;
  background: #fff;
  color: #000;
  min-width: 200px;

  word-wrap: break-word;
  white-space: normal;
  border-radius: 4px;
  min-height: 38px;
  line-height: 14px;
  font-size: 14px;
  cursor: pointer;

  &:hover {
    border: 1px solid rgba(34, 36, 38, 0.35);
  }

  &:focus,
  &:active {
    outline: 0;
  }
}

.product {
  position: relative;
  margin: 10px auto 0;
  display: block;
}

.shop {
  display: block;
  width: 100%;
  position: relative;
  height: 100px;
  border-bottom: 1px solid #f1f1f1;
  color: #444444;
  font-size: 15px;

  :last-of-type {
    border-bottom: none;
  }

  .shop-name {
    display: none;
  }

  span {
    position: absolute;
    top: 50%;
    -webkit-transform: translateY(-50%);
    -moz-transform: translateY(-50%);
    -ms-transform: translateY(-50%);
    -o-transform: translateY(-50%);
    transform: translateY(-50%);
    left: 42%;
    max-width: 42vw;

    &.btn {
      padding: 10px 20px;
      position: absolute;
      display: block;
      left: auto;
      right: 20px;
      top: 50%;
      -webkit-transform: translateY(-50%);
      -moz-transform: translateY(-50%);
      -ms-transform: translateY(-50%);
      -o-transform: translateY(-50%);
      transform: translateY(-50%);
    }
  }

  img {
    max-height: 90%;
    max-width: 90px;
    position: absolute;
    -webkit-transform: translate(0, -50%);
    -moz-transform: translate(0, -50%);
    -ms-transform: translate(0, -50%);
    -o-transform: translate(0, -50%);
    transform: translate(0, -50%);
    top: 50%;
    left: 10px;
    padding: 15px;
  }
}

.default {
  .shop {
    &:after {
      content: "";
      background: url(../assets/next.svg) no-repeat center center;
      position: absolute;
      display: block;
      right: 20px;
      top: 50%;
      width: 20px;
      height: 20px;
      -webkit-transform: translateY(-50%);
      -moz-transform: translateY(-50%);
      -ms-transform: translateY(-50%);
      -o-transform: translateY(-50%);
      transform: translateY(-50%);
    }

    .btn {
      display: none;
    }
  }
}

@media all and (min-width: 650px) {
  p,
  div {
    max-width: 75%;
    text-align: left;
    padding: 1% 4%;
  }

  strong,
  h3 {
    display: block;
    text-align: left;
  }

  .product {
    position: absolute;
    right: 20px;
    top: 20px;
    max-width: 120px;
  }

  .select-wrap,
  select {
    width: auto;
  }

  .shop {
    width: 48%;
    height: auto;
    border: 1px solid #f1f1f1;
    float: left;
    margin: 0 0 10px 1%;
    padding: 15px 0;

    .shop-name {
      display: block;
    }

    img,
    span,
    span.btn {
      position: relative;
      top: auto;
      -webkit-transform: none;
      -moz-transform: none;
      -ms-transform: none;
      -o-transform: none;
      transform: none;
      display: block;
      margin: 0 auto;
      text-align: center;
      left: 0;
    }

    span {
      margin-bottom: 10px;

      &.btn {
        max-width: 50%;
        text-align: center;
        margin: 10px auto 0;
      }
    }
  }

  .default {
    .shop {
      &:after {
        display: none;
      }

      .btn {
        display: block;
      }
    }
  }
}

@media all and (min-width: 650px) and (max-width: 999px) {
  .shop:nth-of-type(2n + 1) {
    clear: both;
  }
}

@media all and (min-width: 1000px) {
  .shop {
    width: 32%;
    margin-left: 1%;

    &:nth-of-type(3n + 1) {
      clear: both;
    }
  }
}

.results {
  &:after {
    content: "";
    display: block;
    clear: both;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/BuyNowAds.vue